import type { DiskAdminFilter } from '../Contracts/DiskAdmin'

/**
 * Адреси адмін-панелі диска (`DISK-006` §5.1–5.2):
 *
 * - `/disk/admin` — огляд компанії;
 * - `/disk/admin/storages[?filter=…]` — список дисків і просторів;
 * - `/disk/admin/storage/<uuid>` — картка простору; `/disk/admin/storage/user:<uuid>` —
 *   співробітник, у якого «Мого диска» ще немає;
 * - `/disk/admin/audit[?storage=…]` — журнал адміністратора;
 * - `/disk/admin/policy` — налаштування диска;
 * - `/disk/admin/offboarding/<uuid>` — справа звільнення (посилання зі
 *   сповіщень `Disk.Offboarding`); до майстра (Ф6) веде в картку простору.
 *
 * ⚠️ Статичний сегмент `admin` перемагає `/disk/:storage/:folder?` екрана
 * диска: react-router ранжує маршрути за специфічністю, а не за порядком.
 */
export const DISK_ADMIN_BASE = '/disk/admin'

/** `entityCode` сповіщень адміністраторам про справу звільнення (`OffboardingNotifier::OFFBOARDING_CODE`). */
export const DISK_OFFBOARDING_ENTITY_CODE = 'Disk.Offboarding'

/** Префікс ключа рядка співробітника без диска. */
export const DISK_ADMIN_USER_KEY = 'user:'

export const diskAdminOverviewUrl = (): string => DISK_ADMIN_BASE

export const diskAdminStoragesUrl = (filter?: DiskAdminFilter): string =>
    filter && filter !== 'all' ? `${DISK_ADMIN_BASE}/storages?filter=${filter}` : `${DISK_ADMIN_BASE}/storages`

/** Картка: uuid простору або ключ рядка (`storage:<uuid>` → uuid, `user:<uuid>` як є). */
export const diskAdminStorageUrl = (idOrKey: string): string => {
    const id = idOrKey.startsWith('storage:') ? idOrKey.slice('storage:'.length) : idOrKey

    return `${DISK_ADMIN_BASE}/storage/${encodeURIComponent(id)}`
}

export const diskAdminAuditUrl = (storage?: string): string =>
    storage ? `${DISK_ADMIN_BASE}/audit?storage=${encodeURIComponent(storage)}` : `${DISK_ADMIN_BASE}/audit`

export const diskAdminPolicyUrl = (): string => `${DISK_ADMIN_BASE}/policy`

export const diskAdminOffboardingUrl = (uuid: string): string =>
    `${DISK_ADMIN_BASE}/offboarding/${encodeURIComponent(uuid)}`

/** Параметр `:id` картки → що відкрити. */
export const parseDiskAdminStorageId = (
    raw: string | undefined
): { kind: 'storage'; uuid: string } | { kind: 'user'; uuid: string } | null => {
    const id = decodeURIComponent(raw ?? '').trim()

    if (id === '') return null
    if (id.startsWith(DISK_ADMIN_USER_KEY)) {
        const uuid = id.slice(DISK_ADMIN_USER_KEY.length)

        return uuid === '' ? null : { kind: 'user', uuid }
    }

    return { kind: 'storage', uuid: id.startsWith('storage:') ? id.slice('storage:'.length) : id }
}
